import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchWaitingTime } from '../api/fetchWaitingTime';
import { Utils } from '@/shared/Utils';
import { format, R } from '@/features/booking/resources/Texts.resx';
import { ConsultationTypeEnum } from '@/shared/enums';

export const useWaitingTime = (
    consultationType: ConsultationTypeEnum,
    isQueueEnabled: boolean,
) => {
    const [canQueueNow, setCanQueueNow] = useState(false);
    const [isQueueOpen, setIsQueueOpen] = useState(false);
    const [waitingTimeFormatted, setWaitingTimeFormatted] = useState<
        string | null
    >(null);
    const [isLongWaitingTime, setIsLongWaitingTime] = useState(false);

    const { refetch, isFetching, error } = useQuery({
        queryKey: ['waitingTime', consultationType],
        queryFn: () => fetchWaitingTime(consultationType),
        enabled: false,
        staleTime: 1000 * 60, // 1 min
    });

    const loadWaitingTime = async () => {
        if (!isQueueEnabled) return;

        const result = await refetch();
        const response = result.data;

        if (!response) return;

        if (!response.IsQueueEnabled) {
            setCanQueueNow(false);
            return;
        }

        setCanQueueNow(response.IsQueueEnabled);
        setIsQueueOpen(response.IsOpen);

        const waitingTimeMinutes = Utils.timeSpanStringToMinutes(
            response.ApproximateWaitingTime,
        );

        setWaitingTimeFormatted(
            Utils.decodeHtml(
                format(R.WaitingTime, waitingTimeMinutes.toString()),
            ),
        );

        setIsLongWaitingTime(response.IsLongWaitingTime);
    };

    useEffect(() => {
        void loadWaitingTime();
    }, []);

    return {
        waitingTimeFormatted,
        isLongWaitingTime,
        canQueueNow,
        isQueueOpen,
        refetch,
        isFetching,
        error,
    };
};
